import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Box, InputAdornment,
  CircularProgress, TablePagination, Link as MuiLink,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import PackageOpenIcon from "@mui/icons-material/Inventory2Outlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNewOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownwardOutlined";
import { apiList, ApiError } from "../api/client";
import ImageGalleryCell from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

function useDebounced(v, ms = 300) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const COLUMNS = [
  { key: "image_urls",           header: "Image",  width: 80,  sortable: false },
  { key: "lot_id",               header: "Lot",    width: 120, sortable: true },
  { key: "reason",               header: "Reason", width: undefined, sortable: true },
  { key: "refund_amount",        header: "Refund", width: 90,  sortable: true, align: "right" },
  { key: "return_shipping_cost", header: "Ship",   width: 90,  sortable: true, align: "right" },
  { key: "return_date",          header: "Date",   width: 110, sortable: true },
  { key: "return_id",            header: "",       width: 60,  sortable: false },
];

export default function Returns() {
  const { error: toastError } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("return_date");
  const [sortDir, setSortDir] = useState("desc");

  const debounced = useDebounced(search, 300);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await apiList("/api/returns", {
        page, page_size: pageSize, q: debounced, sort: sortKey, order: sortDir,
      }, { signal });
      setRows(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load returns", e instanceof ApiError ? e.message : String(e));
      setRows([]); setTotal(0);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, pageSize, debounced, sortKey, sortDir, toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  useEffect(() => { setPage(1); }, [debounced, sortKey, sortDir, pageSize]);

  const onHeaderClick = useCallback((col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir("asc");
    }
  }, [sortKey]);

  const totalLabel = useMemo(
    () => `${total.toLocaleString()} return${total === 1 ? "" : "s"}`,
    [total]
  );

  return (
    <Box>
      {/* Page header — matches OC's standard layout */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Returns
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {totalLabel}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => load()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search reason, lot id, order id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => {
                  const active = sortKey === col.key;
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      style={col.width ? { width: col.width } : undefined}
                      sx={{
                        cursor: col.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      onClick={() => onHeaderClick(col)}
                    >
                      <Stack
                        direction={col.align === "right" ? "row-reverse" : "row"}
                        alignItems="center"
                        gap={0.5}
                        sx={{ display: 'inline-flex' }}
                      >
                        {col.header}
                        {col.sortable && active && (
                          sortDir === "asc"
                            ? <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                            : <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                      <PackageOpenIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No returns</Typography>
                      <Typography variant="caption">All clear — nothing's come back.</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.lot_id ?? row.return_id ?? row.id} hover>
                  <TableCell sx={{ width: 80 }}>
                    <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={48} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'mono', fontSize: '0.78rem' }}>
                    {row.lot_id || "—"}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 0, maxWidth: 420 }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.reason || "—"}
                      </Typography>
                      {row.platform && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={row.platform}
                          sx={{ mt: 0.5, height: 18, fontSize: '0.68rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main' }}>
                    {money(row.refund_amount)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {money(row.return_shipping_cost)}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {row.return_date ? new Date(row.return_date * 1000).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell>
                    {row.return_id ? (
                      <MuiLink
                        href={`https://www.ebay.com/rtn/Return/ReturnsDetail?returnId=${encodeURIComponent(row.return_id)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </MuiLink>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && rows.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
        <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <TablePagination
            component="div"
            count={total}
            page={Math.max(0, page - 1)}
            onPageChange={(_, p) => setPage(p + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Box>
      </Paper>
    </Box>
  );
}
